import {GenericServerError} from '@github-ui/attachments/uploadable'
// eslint-disable-next-line no-restricted-imports
import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {CopilotImageAttacher} from '@github-ui/copilot-chat/utils/copilot-image-attacher'
import {sendRefTelemetry} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import type {CopilotChatModel} from '@github-ui/copilot-chat-types'
import {UploadableFileAttachment} from '@github-ui/copilot-chat-types/uploadable-file-attachment'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import type {
  CopilotSpaceImageResource,
  CopilotSpaceResource,
  CopilotSpaceUploadedTextFileResource,
} from '../../../types'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useCallback} from 'react'

import {useGetCopilotSpaceResourceSizes} from '../../../hooks/use-get-copilot-space-resource-sizes'
import {formatFileSize} from '../utils/format-file-size'
import {UploadableFileExtensions} from '../utils/uploadable-file-extensions'

const IMAGES_NOT_SUPPORTED = 'Images are not yet supported'
const UNEXPECTED_PROCESSING_ERROR = 'An unexpected error occurred while processing a file.'

type UploadResult<T> = {
  errorMessages: string[]
  resources: T[]
}

type FileProcessingResult =
  | {resource: CopilotSpaceImageResource | CopilotSpaceUploadedTextFileResource}
  | {error: string}

interface UseUploadedFileProcessingProps {
  onResourcesAdded: (resources: CopilotSpaceResource[]) => void
  onError?: (errorMessages: string[]) => void
  onUploadingChange?: (uploading: boolean) => void
}

export function useUploadedFileProcessing({
  onResourcesAdded,
  onError,
  onUploadingChange,
}: UseUploadedFileProcessingProps) {
  const {availableModels} = useChatState()
  const {hydrateResourcesWithSizePercentages} = useHydrateResourcesWithSizePercentages()

  const processFiles = useCallback(
    async (files: FileList, {source}: {source: 'drag-and-drop' | 'file-upload'}) => {
      if (!files || files.length === 0) return

      sendFileTelemetry(files, {source})
      onUploadingChange?.(true)
      onError?.([])

      try {
        const {imageFiles, nonImageFiles: potentialTextFiles} = partitionFiles(files, availableModels)

        const [textUploadResult, imageUploadResult] = await Promise.all([
          uploadTextFiles(potentialTextFiles),
          uploadImages(imageFiles, availableModels),
        ])

        const uploadedResources = [...textUploadResult.resources, ...imageUploadResult.resources]
        await hydrateResourcesWithSizePercentages(uploadedResources)
        if (uploadedResources.length > 0) {
          onResourcesAdded(uploadedResources)
          sendRefTelemetry(uploadedResources, source)
        }

        const allErrorMessages = [...textUploadResult.errorMessages, ...imageUploadResult.errorMessages]
        if (allErrorMessages.length > 0) {
          onError?.(allErrorMessages)
        }
      } catch {
        onError?.(['Something went wrong while trying to add the files. Please try again.'])
      } finally {
        onUploadingChange?.(false)
      }
    },
    [availableModels, onResourcesAdded, onError, hydrateResourcesWithSizePercentages, onUploadingChange],
  )

  return {processFiles}
}

function sendFileTelemetry(files: FileList, {source}: {source: 'drag-and-drop' | 'file-upload'}) {
  sendEvent('copilot-spaces.selected_local_files', {fileCount: files.length, source})

  for (const file of Array.from(files)) {
    const extension = getFileExtension(file.name)
    sendEvent('copilot-spaces.selected_local_file', {
      fileExtension: extension,
      contentType: file.type,
      fileSize: file.size,
      source,
    })
  }
}

function useHydrateResourcesWithSizePercentages() {
  const {getCopilotSpaceResourceSizes} = useGetCopilotSpaceResourceSizes()

  const hydrateResourcesWithSizePercentages = useCallback(
    async (resources: CopilotSpaceResource[]) => {
      if (resources.length === 0) return

      const sizeResults = await getCopilotSpaceResourceSizes({resources})
      for (const resource of resources) {
        resource.sizePercentage = sizeResults[resource.id] ?? 0
      }
    },
    [getCopilotSpaceResourceSizes],
  )

  return {hydrateResourcesWithSizePercentages}
}

/**
 * Separates files into image files and non-image files.
 * Image files are identified by their MIME type (starting with 'image/') or by the provided extensions.
 * All other files are considered non-image files and will be processed separately.
 */
function partitionFiles(
  files: FileList,
  availableModels: CopilotChatModel[],
): {imageFiles: File[]; nonImageFiles: File[]} {
  const imageExtensionSet = UploadableFileExtensions.images(availableModels)

  return Array.from(files).reduce<{imageFiles: File[]; nonImageFiles: File[]}>(
    (acc, file) => {
      if (isImageFile(file, imageExtensionSet)) {
        acc.imageFiles.push(file)
      } else {
        acc.nonImageFiles.push(file)
      }
      return acc
    },
    {imageFiles: [], nonImageFiles: []},
  )
}

function isImageFile(file: File, imageExtensionSet: Set<string>): boolean {
  if (file.type.startsWith('image/')) {
    return true
  }

  const fileExtension = getFileExtension(file.name)
  return imageExtensionSet.has(fileExtension)
}

/**
 * Extracts the file extension from a filename, including the dot.
 */
function getFileExtension(filename: string): string {
  const parts = filename.split('.')
  return parts.length > 1 ? `.${parts.pop()?.toLowerCase() ?? ''}` : ''
}

function processUploadResults<T extends CopilotSpaceImageResource | CopilotSpaceUploadedTextFileResource>(
  results: Array<PromiseSettledResult<FileProcessingResult>>,
): UploadResult<T> {
  const resources: T[] = []
  const errorMessages: string[] = []

  for (const result of results) {
    if (result.status === 'fulfilled') {
      if ('resource' in result.value) {
        resources.push(result.value.resource as T)
      } else {
        errorMessages.push(result.value.error)
      }
    } else {
      errorMessages.push(UNEXPECTED_PROCESSING_ERROR)
    }
  }

  return {errorMessages, resources}
}

function areImagesEnabled(): boolean {
  return copilotFeatureFlags.spacesImagesEnabled
}

async function uploadImages(
  files: File[],
  availableModels: ReturnType<typeof useChatState>['availableModels'],
): Promise<UploadResult<CopilotSpaceImageResource>> {
  if (files.length === 0) {
    return {errorMessages: [], resources: []}
  }

  if (!areImagesEnabled()) {
    return {errorMessages: [IMAGES_NOT_SUPPORTED], resources: []}
  }

  const uploadPromises = files.map(file => uploadImage(file, CopilotImageAttacher.getMimeTypes(availableModels)))
  const results = await Promise.allSettled(uploadPromises)

  return processUploadResults(results)
}

async function uploadImage(file: File, allowedTypes: string[]): Promise<FileProcessingResult> {
  try {
    if (file.size > CopilotImageAttacher.getAttachmentSizeLimit()) {
      return {error: imageTooLargeError(file.name)}
    }

    if (!allowedTypes.includes(file.type)) {
      return {error: unsupportedFileTypeError(file.name)}
    }

    const {id: key, attachment: uploadableAttachment} = CopilotImageAttacher.makeImageReference(file, '')
    const asset = (await uploadableAttachment.prefetch()) as {id: number; href: string}

    const resource: CopilotSpaceImageResource = {
      id: key,
      type: 'media_content',
      mediaType: file.type,
      name: file.name || 'Image',
      url: asset.href || '',
      copilotChatAttachmentId: asset.id || 0,
      height: uploadableAttachment.height || 0,
      width: uploadableAttachment.width || 0,
      markedForDestroy: false,
    }

    return {resource}
  } catch (error) {
    return handleImageProcessingError(error, file)
  }
}

async function uploadTextFiles(files: File[]): Promise<UploadResult<CopilotSpaceUploadedTextFileResource>> {
  if (files.length === 0) {
    return {errorMessages: [], resources: []}
  }

  const uploadPromises = files.map(file => uploadTextFile(file))
  const results = await Promise.allSettled(uploadPromises)

  return processUploadResults(results)
}

async function uploadTextFile(file: File): Promise<FileProcessingResult> {
  try {
    if (!file.type.startsWith('text/')) {
      const fileExtension = getFileExtension(file.name)
      if (!UploadableFileExtensions.text.has(fileExtension)) {
        return {error: unsupportedFileTypeError(file.name)}
      }
    }

    file = normalizeTextFileType(file)
    const key = crypto.randomUUID()
    const attachment = await new UploadableFileAttachment(key, file).prefetch()

    const resource: CopilotSpaceUploadedTextFileResource = {
      id: key,
      type: 'uploaded_text_file',
      name: file.name,
      copilotChatAttachmentId: attachment.id,
      url: attachment.href,
      markedForDestroy: false,
    }

    return {resource}
  } catch (error) {
    return handleTextFileProcessingError(error, file)
  }
}

/**
 * Ensures the file has a text type by defaulting to 'text/plain' if needed.
 *
 * The chat attachments endpoint only accepts files with certain type-and-extension combinations.
 * The one exception is text files, which can be uploaded with any extension.
 *
 * This function sets the type to 'text/plain' for cases such as:
 * - Files that don't have a MIME type set by the user's browser
 * - Files that might have a type like `application/csv` which should be treated as a text file
 */
function normalizeTextFileType(file: File): File {
  if (!file.type.startsWith('text/')) {
    return new File([file], file.name, {type: 'text/plain'})
  }

  return file
}

function handleTextFileProcessingError(error: unknown, file: File): FileProcessingResult {
  if (!(error instanceof GenericServerError)) {
    return {error: genericUploadError(file.name)}
  }

  const firstError = error.errors?.[0]

  if (typeof firstError === 'string') {
    return {error: firstError}
  }

  return {error: firstError?.message || genericUploadError(file.name)}
}

function handleImageProcessingError(error: unknown, file: File): FileProcessingResult {
  if (error instanceof Error && error.name === 'FileSizeError') {
    return {error: imageTooLargeError(file.name)}
  }

  return {error: genericUploadError(file.name)}
}

function genericUploadError(fileName: string) {
  return `An error occurred uploading "${fileName}".`
}

function unsupportedFileTypeError(fileName: string) {
  return `File "${fileName}" is not a supported file type.`
}

function imageTooLargeError(fileName: string) {
  const maxSizeFormatted = formatFileSize(CopilotImageAttacher.getAttachmentSizeLimit())
  return `File "${fileName}" is too large. The maximum size for this file type is ${maxSizeFormatted}.`
}
